import React from "react";
import { cn } from "@/lib/utils";
import { KeyTextField, RichTextField } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import Credit from "@/components/molecules/credit";
import { Locales } from "@/lib/i18n";
import StageHeaderHighlight from "./stage-header-highlight";

type StageHeaderProps = {
  locale: Locales;
  title?: KeyTextField;
  description?: RichTextField | KeyTextField;
  children?: React.ReactNode;
  className?: string;
  imageUrl?: string;
  imageAlt?: string;
  hideDescriptionOnMobile?: boolean;
  orientationOnMobile?: "left" | "center";
  isDescriptionCentered?: boolean;
  hideImageOnMobile?: boolean;
  showCredit?: boolean;
  credit?: {
    button?: {
      text: string;
      link?: string;
    };
    image: string;
    description: string;
    name: string;
    state?: "default" | "open";
  };
  highlight?: boolean;
};

const StageHeader = ({
  title,
  description,
  children,
  className,
  imageUrl,
  imageAlt,
  hideDescriptionOnMobile,
  orientationOnMobile = "center",
  hideImageOnMobile = true,
  isDescriptionCentered = true,
  showCredit = false,
  credit,
  locale,
  highlight = false,
}: StageHeaderProps) => {
  const isRichText = (value: unknown): value is RichTextField => Array.isArray(value);

  const paragraphCount =
    highlight && isRichText(description) ? description.filter((block) => block.type === "paragraph").length : 0;

  let renderedParagraphIndex = 0;

  return (
    <section
      className={cn(
        "text-dark-primary grid items-center px-4 md:px-7.5",
        imageUrl ? "gap-8 lg:grid-cols-2" : "place-items-center py-4 md:py-5",
        imageUrl && hideImageOnMobile && "gap-0 md:grid-cols-1 lg:grid-cols-2 lg:gap-10",
        className
      )}
    >
      <header
        className={cn(
          "flex w-full flex-col items-center space-y-3 md:items-start md:space-y-4",
          !imageUrl && "md:items-center",
          hideDescriptionOnMobile && "max-md:space-y-0",
          orientationOnMobile === "left" && "items-start"
        )}
      >
        {children}

        {title && (
          <h1
            className={cn("font-cabinet-grotesk leading-h1 text-start text-2xl font-medium md:text-4xl md:font-normal")}
          >
            {title}
          </h1>
        )}

        <div className={cn(isDescriptionCentered && "md:text-center", "prose")}>
          {description &&
            (isRichText(description) ? (
              <PrismicRichText
                field={description}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="leading-h1 font-cabinet-grotesk mb-2 text-2xl max-md:mt-4 max-md:font-medium md:text-3xl lg:text-4xl">
                      {children}
                    </h2>
                  ),

                  paragraph: ({ children }) => {
                    renderedParagraphIndex += 1;

                    const isLastParagraph = highlight && renderedParagraphIndex === paragraphCount;

                    if (isLastParagraph) {
                      return <StageHeaderHighlight>{children}</StageHeaderHighlight>;
                    }

                    return (
                      <p
                        className={cn(
                          "hidden text-base font-normal md:block",
                          orientationOnMobile === "left" && "block",
                          hideDescriptionOnMobile && "hidden md:block"
                        )}
                      >
                        {children}
                      </p>
                    );
                  },
                }}
              />
            ) : (
              <p className="hidden max-w-3xl text-base font-normal md:block">{description}</p>
            ))}
        </div>
      </header>

      {imageUrl && (
        <figure
          className={cn(
            "relative aspect-square size-full max-h-[500px] max-w-[750px] overflow-hidden rounded-lg",
            hideImageOnMobile && "max-lg:hidden"
          )}
        >
          <Image src={imageUrl} alt={imageAlt || "Header Image"} fill className="absolute object-cover" />

          {showCredit && credit && (
            <Credit
              locale={locale}
              showRightArrow={false}
              className="absolute bottom-2.5 left-6 w-fit"
              button={credit.button}
              description={credit.description}
              state={credit.state ?? "default"}
              image={credit.image}
              name={credit.name}
            />
          )}
        </figure>
      )}
    </section>
  );
};

export default StageHeader;
